import React, { useState, useEffect, useCallback } from 'react';
import { FaStar } from 'react-icons/fa';
import ReviewModal from './ReviewModal';

function OrderHistoryPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedOrderForReview, setSelectedOrderForReview] = useState(null);
  const [selectedProductForReview, setSelectedProductForReview] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'processing', 'delivered'

  const API_BASE_URL = 'https://aman-life-style-bd-main-server-974485829900.asia-south1.run.app';

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError("Not logged in. Please log in.");
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/orders`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        setError("Your session has expired. Please log in again.");
        setLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setOrders(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Processing':
        return 'border-yellow-500';
      case 'Packaging':
        return 'border-blue-500';
      case 'Cancel':
        return 'border-red-500';
      case 'Return':
        return 'border-gray-500';
      case 'Confirm':
        return 'border-green-500';
      case 'Handed over to Courier':
        return 'border-purple-500';
      case 'On the Way':
        return 'border-purple-500';
      case 'Delivered':
        return 'border-green-600';
      default:
        return 'border-gray-300';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'Processing':
        return 'bg-yellow-100 text-yellow-800';
      case 'Packaging':
        return 'bg-blue-100 text-blue-800';
      case 'Cancel':
        return 'bg-red-100 text-red-800';
      case 'Return':
        return 'bg-gray-100 text-gray-800';
      case 'Confirm':
        return 'bg-green-100 text-green-800';
      case 'Handed over to Courier':
        return 'bg-purple-100 text-purple-800';
      case 'On the Way':
        return 'bg-purple-100 text-purple-800';
      case 'Delivered':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTranslatedStatus = (status) => {
    switch (status) {
      case 'Processing':
        return 'Processing';
      case 'Packaging':
        return 'Packaging';
      case 'Cancel':
        return 'Cancelled';
      case 'Return':
        return 'Returned';
      case 'Confirm':
        return 'Confirmed';
      case 'Handed over to Courier':
        return 'Handed over to Courier';
      case 'On the Way':
        return 'On the Way';
      case 'Delivered':
        return 'Delivered';
      default:
        return status;
    }
  };

  const filteredOrders = orders.filter(order => {
    if (filter === 'all') return true;
    if (filter === 'processing' && (order.status === 'Processing' || order.status === 'On the Way' || order.status === 'Handed over to Courier' || order.status === 'Packaging' || order.status === 'Confirm')) {
      return true;
    }
    if (filter === 'delivered' && order.status === 'Delivered') {
      return true;
    }
    return false;
  });

  const handleAddReviewClick = (order, item) => {
    setSelectedOrderForReview(order);
    setSelectedProductForReview(item);
    setShowReviewModal(true);
  };

  const handleReviewSubmit = async (rating, comment) => {
    if (selectedOrderForReview && selectedProductForReview) {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          alert("Not logged in. Please log in.");
          setShowReviewModal(false);
          return;
        }

        const customerName = selectedOrderForReview.customerInfo?.name || 'Anonymous';

        const response = await fetch(`${API_BASE_URL}/api/add-review`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            orderId: selectedOrderForReview.orderId,
            productId: selectedProductForReview.productId,
            rating,
            comment,
            customerName: customerName,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to add review.');
        }

        alert('Review added successfully!');
        fetchOrders();
      } catch (err) {
        console.error("Error submitting review:", err);
        alert(`Failed to add review: ${err.message}`);
      }
    }
    setShowReviewModal(false);
    setSelectedOrderForReview(null);
    setSelectedProductForReview(null);
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center text-gray-600 text-lg">Loading orders...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center text-red-600 text-lg">Error loading data: {error}</div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Your Orders</h1>
        </div>

        {/* Filter Buttons */}
        <div className="bg-white rounded-lg shadow-sm mb-6 p-4">
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setFilter('all')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${filter === 'all'
                ? 'bg-amazon-orange text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
            >
              All Orders
            </button>
            <button
              onClick={() => setFilter('processing')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${filter === 'processing'
                ? 'bg-amazon-orange text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
            >
              In Progress
            </button>
            <button
              onClick={() => setFilter('delivered')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${filter === 'delivered'
                ? 'bg-amazon-orange text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
            >
              Delivered
            </button>
          </div>
        </div>

        {/* Orders List */}
        {filteredOrders.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <p className="text-gray-600 text-lg">No orders found.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredOrders.map((order) => (
              <div
                key={order.orderId}
                className={`bg-white rounded-lg shadow-sm overflow-hidden border-l-4 ${getStatusColor(order.status)}`}
              >
                {/* Order Header */}
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex flex-wrap items-center gap-6 text-sm">
                      <div>
                        <span className="text-gray-600">ORDER PLACED</span>
                        <p className="font-medium text-gray-900">{order.orderDate}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">TOTAL</span>
                        <p className="font-medium text-gray-900">৳{order.totalAmount}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">ORDER #</span>
                        <p className="font-medium text-gray-900">{order.orderId}</p>
                      </div>
                    </div>
                    <div>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(order.status)}`}>
                        {getTranslatedStatus(order.status)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Order Details */}
                <div className="px-6 py-4">
                  <div className="mb-3">
                    <span className="text-sm text-gray-600">Payment Method: </span>
                    <span className="text-sm font-medium text-gray-900">
                      {order.paymentMethod === 'cashOnDelivery' ? 'Cash on Delivery' : order.paymentMethod === 'bkash' ? 'bKash' : 'Nagad'}
                    </span>
                  </div>

                  {/* Order Items */}
                  <div className="space-y-4">
                    {order.items.map((item) => (
                      <div key={item.productId} className="border-t border-gray-200 pt-4">
                        <div className="flex gap-4">
                          {/* Product Image */}
                          <div className="flex-shrink-0">
                            <img
                              src={item.image}
                              alt={item.name}
                              className="w-24 h-24 object-cover rounded-md border border-gray-200"
                            />
                          </div>

                          {/* Product Details */}
                          <div className="flex-grow">
                            <h3 className="text-base font-medium text-gray-900 hover:text-amazon-orange cursor-pointer">
                              {item.name}
                            </h3>
                            <div className="mt-2 text-sm text-gray-600">
                              <p>Quantity: {item.quantity}</p>
                              <p className="font-medium text-gray-900">৳{item.price * item.quantity}</p>
                            </div>

                            {/* Review Section */}
                            <div className="mt-4">
                              {item.review && item.review.rating && item.review.comment ? (
                                <div className="bg-green-50 border border-green-200 rounded-md p-3">
                                  <p className="text-xs font-medium text-green-800 mb-2">Your Review:</p>
                                  <div className="flex items-center gap-2 mb-2">
                                    <div className="flex items-center text-yellow-500">
                                      {[...Array(item.review.rating)].map((_, i) => (
                                        <FaStar key={i} className="w-4 h-4" />
                                      ))}
                                    </div>
                                    <span className="text-sm font-medium text-gray-700">
                                      ({item.review.rating}/5)
                                    </span>
                                  </div>
                                  <p className="text-sm text-gray-700">"{item.review.comment}"</p>
                                </div>
                              ) : (
                                order.status === 'Delivered' && (
                                  <button
                                    onClick={() => handleAddReviewClick(order, item)}
                                    className="px-4 py-2 bg-amazon-yellow hover:bg-yellow-500 text-gray-900 text-sm font-medium rounded-md transition-colors border border-yellow-600"
                                  >
                                    Write a product review
                                  </button>
                                )
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Review Modal */}
      {showReviewModal && selectedOrderForReview && selectedProductForReview && (
        <ReviewModal
          onClose={() => setShowReviewModal(false)}
          onSubmit={handleReviewSubmit}
          productName={selectedProductForReview.name}
        />
      )}

      {/* Custom CSS for Amazon colors */}
      <style jsx>{`
        .bg-amazon-orange {
          background-color: #ff9900;
        }
        .text-amazon-orange {
          color: #ff9900;
        }
        .bg-amazon-yellow {
          background-color: #ffd814;
        }
        .hover\\:bg-yellow-500:hover {
          background-color: #f7ca00;
        }
      `}</style>
    </div>
  );
}

export default OrderHistoryPage;